/**
 * Tenant passkeys — query + revoke mutation.
 *
 * The tenant-wide credential inventory lives on the platform
 * tenant-admin tree:
 *   GET  /api/v1/tenants/{tenant_id}/credentials
 *   POST /api/v1/tenants/{tenant_id}/credentials/{id}/revoke
 *
 * On a tenant host, apiFetch's rewritePathForHost maps these
 * to /api/tenant/v1/credentials automatically.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { withStepUp } from "@/lib/with-step-up";
import {
  tenantPasskeysListResponseSchema,
  type PasskeysListSearch,
  type TenantPasskeysListResponse,
} from "./schemas";

function base(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/credentials`;
}

export async function fetchTenantPasskeys(
  tenantID: string,
  search: PasskeysListSearch,
): Promise<TenantPasskeysListResponse> {
  const raw = await apiFetch<unknown>(base(tenantID), {
    searchParams: {
      q: search.q,
      user_id: search.user_id,
      backup_state: search.backup_state,
      sort_by: search.sortBy,
      sort_order: search.sortOrder,
      offset: search.offset,
    },
  });
  return tenantPasskeysListResponseSchema.parse(raw);
}

export function useTenantPasskeysQuery(
  tenantID: string,
  search: PasskeysListSearch,
) {
  return useQuery<TenantPasskeysListResponse, ApiError>({
    queryKey: queryKeys.passkeys.list(tenantID, search),
    queryFn: () => fetchTenantPasskeys(tenantID, search),
    enabled: tenantID.length > 0,
  });
}

/**
 * Revoke one passkey.
 *
 * `force` acknowledges the tenant's method floor: without it the server refuses
 * a revoke that would leave the owner unable to sign in, and the page turns that
 * refusal into a second confirmation. See lib/method-floor-override.ts — the
 * flag must never be sent on a first attempt, or the operator is never warned.
 */
export function useRevokeTenantPasskeyMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { passkeyId: string; force?: boolean }>({
    mutationFn: ({ passkeyId, force }) =>
      withStepUp(() =>
        apiFetch<void>(
          `${base(tenantID)}/${encodeURIComponent(passkeyId)}/revoke`,
          { method: "POST", searchParams: force ? { force: "true" } : undefined },
        ),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.passkeys.all(tenantID) });
      qc.invalidateQueries({ queryKey: queryKeys.users.all(tenantID) });
    },
  });
}
